import React, { useEffect, useMemo, useRef } from "react";

import { Alert, Button, CircularProgress, Stack } from "@mui/material";

import DynamicFieldTable from "components/organisms/DynamicFieldTable";

import {
    areOpenAiMappingsValid,
    getConversionLabel,
} from "./OpenAiAds.helpers";

const EMPTY_MAPPING = {
    conversion_id: "",
    event: "",
    conversion_name: "",
};
const CUSTOM_EVENT_VALUE = "__custom_event__";
const CUSTOM_EVENT_OPTION = {
    label: "Custom event",
    value: CUSTOM_EVENT_VALUE,
};

const OpenAiAdsMappingContent = ({
    conversions,
    events,
    selectedMappings,
    setSelectedMappings,
    loadError,
    onRetry,
    isLoading,
    isScrollable = false,
    maxHeight = null,
}) => {
    const rows = selectedMappings ?? [];
    const didSeedInitialRow = useRef(false);

    useEffect(() => {
        if (isLoading || loadError || didSeedInitialRow.current) return;
        if (!events?.length || !conversions?.length) return;

        didSeedInitialRow.current = true;
        if (!selectedMappings?.length) {
            setSelectedMappings([{ ...EMPTY_MAPPING }]);
        }
    }, [
        isLoading,
        loadError,
        events,
        conversions,
        selectedMappings,
        setSelectedMappings,
    ]);

    const conversionOptions = useMemo(
        () =>
            (conversions ?? []).map((conversion) => ({
                label: getConversionLabel(conversion),
                value: conversion?.id?.toString(),
            })),
        [conversions],
    );

    const transformedFields = useMemo(() => {
        const usedEvents = rows.map((mapping) => mapping.event);
        const usedConversionIds = rows.map((mapping) =>
            mapping.conversion_id?.toString(),
        );

        return rows.map((mapping) => ({
            event: {
                value: mapping.event || "",
                variant: mapping.isCustom ? "TEXT" : "AUTOCOMPLETE",
                autoFocus: mapping.focusEventInput === true,
                placeholder: mapping.isCustom
                    ? "Enter an event"
                    : "Select an event",
                options: [
                    ...(events ?? [])
                        .filter(
                            (event) =>
                                !event.isCustom &&
                                (event.code === mapping.event ||
                                    !usedEvents.includes(event.code)),
                        )
                        .map((event) => ({
                            label: event.name,
                            value: event.code,
                        })),
                    CUSTOM_EVENT_OPTION,
                ],
            },
            conversion_id: {
                value: mapping.conversion_id || "",
                options: conversionOptions.filter(
                    (conversion) =>
                        conversion.value ===
                            mapping.conversion_id?.toString() ||
                        !usedConversionIds.includes(conversion.value),
                ),
            },
        }));
    }, [rows, events, conversionOptions]);

    const handleFieldsChange = (newFields) => {
        setSelectedMappings(
            newFields.map((field) => {
                const conversionId =
                    field.conversion_id?.value?.toString().trim() || "";
                const conversion = (conversions ?? []).find(
                    (item) => item?.id?.toString() === conversionId,
                );
                const selectedEvent =
                    field.event?.value?.toString().trim() || "";
                const isCustomOption = selectedEvent === CUSTOM_EVENT_VALUE;
                const event = (events ?? []).find(
                    (item) => item?.code?.toString() === selectedEvent,
                );
                const isCustom =
                    field.event?.variant === "TEXT" ||
                    isCustomOption ||
                    event?.isCustom === true;

                return {
                    conversion_id: conversionId,
                    event: isCustomOption ? "" : selectedEvent,
                    isCustom,
                    focusEventInput:
                        isCustomOption || field.event?.autoFocus === true,
                    conversion_name: conversion
                        ? (conversion.name ?? conversion.conversion_name ?? "")
                        : "",
                    event_type: conversion?.event_type ?? "",
                    custom_event_name: conversion?.custom_event_name ?? null,
                };
            }),
        );
    };

    const fieldsConfiguration = [
        {
            name: "event",
            label: "Internal Event",
            variant: "AUTOCOMPLETE",
            placeholder: "Select an event",
        },
        {
            name: "conversion_id",
            label: "OpenAI Ads Conversion",
            variant: "AUTOCOMPLETE",
            placeholder: "Select Conversion",
        },
    ];

    const hasInvalidMapping =
        rows.length > 0 && !areOpenAiMappingsValid(rows, conversions);

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" py={4}>
                <CircularProgress size={20} />
            </Stack>
        );
    }

    if (loadError) {
        return (
            <Alert
                severity="error"
                action={
                    onRetry && (
                        <Button
                            color="error"
                            variant="contained"
                            size="small"
                            onClick={onRetry}
                        >
                            Retry
                        </Button>
                    )
                }
            >
                OpenAI Ads conversions could not be loaded: {loadError}
            </Alert>
        );
    }

    if (!conversions?.length) {
        return (
            <Alert severity="info">
                This OpenAI Ads pixel has no conversions to map yet. Saving with
                no rows clears any stored mappings.
            </Alert>
        );
    }

    return (
        <Stack spacing={2}>
            <p>
                Map your internal events to OpenAI Ads conversions. You can pick
                a catalog event or enter a custom event that is not in the
                catalog.
            </p>

            <DynamicFieldTable
                fields={transformedFields}
                setFields={handleFieldsChange}
                fieldsConfiguration={fieldsConfiguration}
                minLength={0}
                addMoreDisabled={false}
                addMoreTooltip=""
                showStatus={false}
                isScrollable={isScrollable}
                maxHeight={maxHeight}
            />
            {hasInvalidMapping && (
                <Alert severity="warning">
                    Select an event and OpenAI Ads conversion for every row, or
                    remove the incomplete row.
                </Alert>
            )}
        </Stack>
    );
};

export default OpenAiAdsMappingContent;
